import React, { useRef, useState } from 'react';
import { ConfigProvider, Button } from 'antd';
import {
  CloudUploadOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  InboxOutlined,
  DownloadOutlined,
  DeleteOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import {
  uploadHsFile,
  validateFile,
  ACCEPTED_EXTENSIONS,
  MAX_FILE_SIZE,
} from '../../api/uploadApi';
import './UploadHsFilePage.css';

const MAX_RECENT = 5;

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const UploadHsFilePage = () => {
  const fileInputRef = useRef(null);

  /* ── File selection state ── */
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');

  /* ── Upload state ── */
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  /* ── Success/drag state ── */
  const [isDragging, setIsDragging] = useState(false);
  const [recentFiles, setRecentFiles] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  /* ── File handling ── */
  const handleFileChange = (file) => {
    setFileError('');
    setUploadError('');
    setSuccessMessage('');

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const { valid, message } = validateFile(file);
    if (!valid) {
      setFileError(message);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const handleInputChange = (e) => {
    handleFileChange(e.target.files?.[0] || null);
  };

  /* ── Drag & Drop ── */
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files?.[0] || null);
  };

  /* ── Clear ── */
  const clearFile = () => {
    setSelectedFile(null);
    setFileError('');
    setUploadError('');
    setSuccessMessage('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /* ── Submit ── */
  const handleUpload = async () => {
    if (!selectedFile) {
      setFileError('Please select a file before uploading.');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    setSuccessMessage('');

    try {
      const fileInfo = await uploadHsFile(selectedFile);

      setSuccessMessage(`"${fileInfo.name || selectedFile.name}" uploaded successfully!`);
      clearFile();

      setRecentFiles((prev) => {
        const entry = {
          id: Date.now(),
          name: fileInfo.name || selectedFile.name,
          size: fileInfo.size || formatSize(selectedFile.size),
          url: fileInfo.url || '#',
          uploaded_at: fileInfo.uploaded_at || 'just now',
        };
        return [entry, ...prev].slice(0, MAX_RECENT);
      });
    } catch (err) {
      setUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const maxMB = (MAX_FILE_SIZE / 1024 / 1024).toFixed(0);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#3b82f6',
          borderRadius: 8,
          fontFamily: "'Gabarito', sans-serif",
        },
      }}
    >
      <div className="upload-root">
        {/* ── Scrollable body ── */}
        <div className="upload-body">
          <div className="upload-center-col">

            {/* ── Success toast ── */}
            {successMessage && (
              <div className="upload-success-banner">
                <CheckCircleFilled className="upload-success-icon" />
                <span>{successMessage}</span>
              </div>
            )}

            {/* ── Upload card ── */}
            <div className="upload-card">
              <div className="upload-card-header">
                <FileImageOutlined className="upload-card-icon" />
                <div>
                  <div className="upload-card-title">Select File</div>
                  <div className="upload-card-sub">
                    Accepted: PNG, JPG, JPEG, WebP &nbsp;·&nbsp; Max: {maxMB} MB
                  </div>
                </div>
              </div>

              {/* ── Drop Zone ── */}
              <div
                className={`upload-dropzone${isDragging ? ' upload-dropzone--active' : ''}${selectedFile ? ' upload-dropzone--has-file' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !selectedFile && fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && !selectedFile && fileInputRef.current?.click()}
                aria-label="File drop zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_EXTENSIONS}
                  onChange={handleInputChange}
                  className="upload-hidden-input"
                  aria-label="File input"
                />

                {selectedFile ? (
                  <div className="upload-file-preview">
                    <div className="upload-file-thumb">
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt="Preview"
                        className="upload-thumb-img"
                      />
                    </div>
                    <div className="upload-file-meta">
                      <span className="upload-file-name">{selectedFile.name}</span>
                      <span className="upload-file-size">{formatSize(selectedFile.size)}</span>
                    </div>
                    <button
                      className="upload-clear-btn"
                      onClick={(e) => { e.stopPropagation(); clearFile(); }}
                      aria-label="Remove file"
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                ) : (
                  <div className="upload-dropzone-idle">
                    <InboxOutlined className="upload-inbox-icon" />
                    <span className="upload-dropzone-headline">
                      {isDragging ? 'Drop it here!' : 'Drag & drop or click to browse'}
                    </span>
                    <span className="upload-dropzone-hint">
                      PNG, JPG, JPEG, WebP up to {maxMB} MB
                    </span>
                  </div>
                )}
              </div>

              {/* ── Validation error ── */}
              {fileError && (
                <div className="upload-field-error">
                  <CloseCircleFilled /> {fileError}
                </div>
              )}

              {/* ── Upload error ── */}
              {uploadError && (
                <div className="upload-field-error upload-field-error--network">
                  <CloseCircleFilled /> {uploadError}
                </div>
              )}

              {/* ── Actions ── */}
              <div className="upload-actions">
                <Button
                  type="primary"
                  size="large"
                  icon={<CloudUploadOutlined />}
                  loading={isUploading}
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading}
                  className="upload-submit-btn"
                  block
                >
                  {isUploading ? 'Uploading…' : 'Upload File'}
                </Button>

                {selectedFile && !isUploading && (
                  <button className="upload-cancel-btn" onClick={clearFile}>
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* ── Recently Uploaded ── */}
            {recentFiles.length > 0 && (
              <div className="upload-recent-card">
                <div className="upload-recent-header">
                  <CheckCircleFilled className="upload-recent-check" />
                  <span className="upload-recent-title">Recently Uploaded</span>
                  <span className="upload-recent-count">{recentFiles.length}</span>
                </div>

                <div className="upload-recent-list">
                  {recentFiles.map((f) => (
                    <div key={f.id} className="upload-recent-item">
                      <div className="upload-recent-item-info">
                        <FileImageOutlined className="upload-recent-item-icon" />
                        <div>
                          <div className="upload-recent-item-name">{f.name}</div>
                          <div className="upload-recent-item-meta">
                            {f.size}&nbsp;·&nbsp;{f.uploaded_at}
                          </div>
                        </div>
                      </div>
                      {f.url && f.url !== '#' && (
                        <a
                          href={f.url}
                          download
                          className="upload-recent-download"
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Download file"
                        >
                          <DownloadOutlined />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default UploadHsFilePage;
